import styled from "@emotion/styled";
import {
	Avatar,
	Box,
	Divider,
	ListItemButton,
	ListItemText,
	Typography,
	useMediaQuery,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {useViewController} from "data/services/locator/locator_hook.service";
import {observer} from "mobx-react";
import React, {useCallback} from "react";
import {NavLink, useLocation} from "react-router-dom";
import {Exist} from "views/components/exist/exist.component";
import {IHeaderController} from "views/components/header/header.controller";
import {Container} from "views/components/structure/structure.component";
import {Bindings} from "data/constants/bindings";
import {MAIN_URL, TEAM_DATA} from "data/constants";
import {theme} from "data/constants/theme";
import {BurgerButton} from "views/components/cfl_header/common";
import {ReactComponent as BurgerIcon} from "assets/img/icons/burger.svg";
import {ReactComponent as CloseIcon} from "assets/img/icons/close.svg";
import {SponsorLogo} from "views/components/sponsor_logo/sponsor_logo.component";

const mobileViewQuery = `max-width: 970px`;

export const NavigationWrapper = styled.div<{isOpen: boolean}>`
	display: flex;
	flex-direction: column;
	position: fixed;
	width: 100vw;
	height: 100%;
	left: 0;
	top: 0;
	z-index: 100;
	text-align: left;
	overflow: hidden;
	animation: ${({isOpen}) => (isOpen ? "showMenu" : "hideMenu")} forwards 0.3s ease-out;

	@keyframes showMenu {
		0% {
			display: block;
			background-color: #1c1c1c00;
		}

		99% {
			background-color: #1c1c1c7f;
		}

		100% {
			background-color: #1c1c1c7f;
		}
	}

	@keyframes hideMenu {
		0% {
			display: block;
		}

		99% {
			display: none;
			width: 100%;
		}

		100% {
			display: none;
			width: 0;
		}
	}
`;

export const Navigation = styled.div<{isOpen: boolean}>`
	width: 270px;
	padding-bottom: 20px;
	transition: transform 0.3s ease-out;
	background: #202020;
	display: block;
	height: 100%;
	left: 0;
	top: 0;
	overflow: auto;
	overflow-y: hidden;
	transform: ${({isOpen}) => (isOpen ? "translateZ(0)" : "translate3d(-270px, 0, 0)")};
`;

const HeaderWrapper = styled.div`
	background: ${theme.palette.primary.main};
	font-family: var(--fontFamilyBase);
	color: var(--textColor4);
	text-transform: uppercase;
	font-weight: 600;
	font-size: 16px;
	line-height: 140%;
`;

const Wrapper = styled(Container)`
	display: flex;
	justify-content: flex-start;
	height: 63.5px;

	@media (${mobileViewQuery}) {
		padding: 8px 16px;
		min-height: 82px;
		height: auto;
	}
`;

const GameLogoLink = styled(NavLink)`
	display: flex;
	align-items: center;
	justify-content: center;
	padding-right: 12px;
	@media (${mobileViewQuery}) {
		margin: 0 auto 0 0;
	}

	h1 {
		color: #fff;
		text-transform: uppercase;
	}
`;

const GameLogo = styled.img`
	//max-height: 60px;
	max-height: 44px;
	max-width: 100%;
	margin-right: 10px;

	@media (max-width: 480px) {
		max-width: 90%;
	}
`;

const MenuList = styled.div`
	display: flex;

	//button {
	//	outline: none;
	//	border: none;
	//	background: none;
	//	text-transform: uppercase;
	//	font-weight: 500;
	//	font-size: 18px;
	//	line-height: 20px;
	//	color: #fff;
	//	opacity: 0.9;
	//}

	a {
		color: #fff;
		opacity: 0.9;
	}
`;

const Menu = styled.menu`
	margin-right: auto;
	display: flex;
	flex: 1;
`;

const BoxWrapper = styled.div`
	width: 240px;
	display: flex;
	align-items: flex-start;
	justify-content: flex-start;
	height: 100%;
	flex-direction: column;
`;

const ListItemFull = styled(ListItemButton)`
	width: 100%;
	height: 48px;
	position: relative;

	svg {
		margin-right: 20px;
	}

	@media (${mobileViewQuery}) {
		&.gamezone {
			bottom: 100px;

			margin-bottom: 40px;
		}
	}
`;

const ListItemUser = styled(ListItemFull)`
	height: 72px;
	padding-bottom: 16px;
	padding-top: 16px;
`;

const ListFull = styled.div`
	width: 100%;
`;

const GamezoneLink = styled.a`
	margin: 0 12px;
	white-space: nowrap;
`;

const RightSide = styled.div`
	display: flex;
	align-items: center;
	position: relative;
`;

const Positioned = styled.div`
	position: absolute;
	top: 100%;
	left: 0;
	display: none;
	flex-flow: column;
	background: #fff;
	color: var(--textColor7);
	box-shadow: 0 0.1px 0.3px rgba(28, 28, 30, 0.1), 0 1px 2px rgba(28, 28, 30, 0.2);
	border-radius: 0 0 4px 4px;
	width: max-content;
	min-width: 100%;
	z-index: 2;

	a {
		white-space: nowrap;
		font-weight: 500;
		font-size: 14px;
		line-height: 20px;
		opacity: 1;
		padding: 10px 10px 10px 10px;
		color: #fff;

		&:last-child {
			border: none;
		}

		&:hover {
			color: var(--textColor7);
		}

		&.active {
			color: var(--textColor7);
		}
	}

	& > div {
		border-bottom: 1px solid #cccccc;

		&:last-child {
			border-bottom: none;
		}
	}
`;

const RelativeBox = styled(Box)`
	position: relative;
	display: flex;
	align-items: center;
	cursor: pointer;

	a,
	span {
		white-space: nowrap;
		font-family: var(--fontFamilyBase);
		padding: 20px;
		//min-width: 110px;
	}

	&:hover {
		.sub-menu {
			display: flex;
		}

		color: #000;
		background: #fff;

		a,
		button {
			color: #000;
		}
	}

	:has(a.active) {
		color: #000;
		background: #fff;

		a {
			color: #000;
		}
	}
`;
const Submenu = styled.div`
	padding-left: 20px;
`;

const LinkSpan = styled(Typography)`
	line-height: 32px;
	margin-left: 20px;
`;

export const Header: React.FC = observer(() => {
	const isMobileView = useMediaQuery(`(${mobileViewQuery})`);
	const controller = useViewController<IHeaderController>(Bindings.HeaderController, {
		location: useLocation(),
	});
	const {i18n, gameZoneLink, staticPagesMenuList, toggleDrawer, isOpenDrawer} = controller;
	// const showDrawer = () => controller.toggleDrawer(true);
	const hideDrawer = () => controller.toggleDrawer(false);

	const toggleMenu = useCallback(() => {
		toggleDrawer(!isOpenDrawer);
	}, [toggleDrawer, isOpenDrawer]);

	const clickHandler = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
	}, []);

	return (
		<HeaderWrapper>
			<Wrapper>
				<Exist when={isMobileView}>
					<BurgerButton onClick={toggleMenu}>
						{isOpenDrawer ? <CloseIcon /> : <BurgerIcon />}
					</BurgerButton>
					<NavigationWrapper isOpen={controller.isOpenDrawer} onClick={hideDrawer}>
						<Navigation isOpen={controller.isOpenDrawer} onClick={clickHandler}>
							<BoxWrapper>
								<ListFull>
									<Exist when={controller.isAuthorized}>
										<a href={`${MAIN_URL}profile`} onClick={hideDrawer}>
											<ListItemUser>
												<Avatar
													sx={{
														backgroundColor: "var(--textColor5)",
														color: "#FFF",
													}}>
													{controller.userInitials}
												</Avatar>
												<ListItemText sx={{marginLeft: "15px"}}>
													{controller.userFullName}
												</ListItemText>
											</ListItemUser>
										</a>
										<Divider sx={{borderColor: "var(--background4)"}} />
									</Exist>
									<Exist when={controller.isAuthorized}>
										{controller.mainMenuList.map((item, index) => (
											<div key={index}>
												<ListItemButton
													component={NavLink}
													to={item.url}
													onClick={hideDrawer}>
													<LinkSpan>{item.label.toUpperCase()}</LinkSpan>
												</ListItemButton>

												<Exist when={item.children}>
													<Submenu>
														{item.children?.map((subMenuItem) => (
															<ListItemButton
																component={NavLink}
																to={subMenuItem.url}
																key={subMenuItem.url}
																onClick={hideDrawer}>
																<LinkSpan>
																	{subMenuItem.label.toUpperCase()}
																</LinkSpan>
															</ListItemButton>
														))}
													</Submenu>
												</Exist>
											</div>
										))}
									</Exist>
									<Exist when={!controller.isAuthorized}>
										<ListItemButton
											component={NavLink}
											to="/"
											onClick={hideDrawer}>
											<LinkSpan>
												{i18n.t("nav.main.home", "Home").toUpperCase()}
											</LinkSpan>
										</ListItemButton>
										{controller.guestMenuList.map((item, index) => (
											<ListItemButton
												key={index}
												onClick={() => {
													item.clickEvent?.();
													hideDrawer();
												}}>
												<LinkSpan>{item.label.toUpperCase()}</LinkSpan>
											</ListItemButton>
										))}
									</Exist>
									<Divider sx={{borderColor: "var(--background4)"}} />
									{staticPagesMenuList.map(({label, url}, index) =>
										url.includes("https") ? (
											<ListItemButton key={index} href={url}>
												<LinkSpan>{label.toUpperCase()}</LinkSpan>
											</ListItemButton>
										) : (
											<ListItemButton
												key={index}
												component={NavLink}
												to={url}
												onClick={hideDrawer}>
												<LinkSpan>{label.toUpperCase()}</LinkSpan>
											</ListItemButton>
										)
									)}
									<Exist when={controller.isAuthorized}>
										<ListItemButton href={`${MAIN_URL}profile`} component={"a"}>
											<LinkSpan>
												{i18n
													.t("nav.main.my_account", "My Account")
													.toUpperCase()}
											</LinkSpan>
										</ListItemButton>
									</Exist>
								</ListFull>
							</BoxWrapper>
							<ListItemFull className="gamezone">
								<GamezoneLink href={gameZoneLink}>
									{i18n.t("nav.main.gamezone", "Gamezone").toUpperCase()}
								</GamezoneLink>
							</ListItemFull>
						</Navigation>
					</NavigationWrapper>
				</Exist>
				<GameLogoLink to={controller.rootPath} className="header-team-name">
					<GameLogo src={TEAM_DATA.altLogo || TEAM_DATA.logo} alt="Game Zone logo" />
					<Typography
						variant={"header"}
						sx={(theme) => ({
							[theme.breakpoints.down(480)]: {
								fontSize: "24px",
							},
						})}>
						{i18n.t(TEAM_DATA.name)}
					</Typography>
				</GameLogoLink>
				<Exist when={!isMobileView}>
					<Menu>
						<MenuList>
							{controller.isAuthorized
								? controller.mainMenuList.map((item, index) => (
										<RelativeBox key={index}>
											<NavLink to={item.url}>{item.label}</NavLink>

											<Exist when={item.children}>
												<ArrowDropDownIcon
													style={{position: "relative", right: 15}}
												/>

												<Positioned className={"sub-menu"}>
													{item.children?.map((it, index) => (
														<ListItemButton
															component={NavLink}
															to={it.url}
															key={index}>
															{it.label}
														</ListItemButton>
													))}
												</Positioned>
											</Exist>
										</RelativeBox>
								  ))
								: null}
							<Exist when={controller.isAuthorized}>
								<RelativeBox>
									<a href={`${MAIN_URL}profile`}>
										{" "}
										{i18n.t("nav.main.my_account", "My Account").toUpperCase()}
									</a>
								</RelativeBox>
							</Exist>
							{staticPagesMenuList.map(({label, url}, index) => (
								<RelativeBox key={index}>
									<NavLink to={url}>{label}</NavLink>
								</RelativeBox>
							))}
						</MenuList>
					</Menu>
				</Exist>
				<RightSide>
					<SponsorLogo width="112px" height="40px" />
				</RightSide>
			</Wrapper>
		</HeaderWrapper>
	);
});
